import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Package } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { usePurchaseOrder, useUpdatePurchaseOrder, useReceivePurchaseOrder } from '@/hooks/use-purchase-orders';
import { PurchaseOrderForm } from './purchase-order-form';
import { WarehouseOwnerPurchaseOrderView } from './warehouse-owner-po-view';
import { usePermissions } from '@/hooks/use-permissions';

interface PurchaseOrderEditPageProps {
  orderId: string;
}

export function PurchaseOrderEditPage({ orderId }: PurchaseOrderEditPageProps) {
  const { data, isLoading } = usePurchaseOrder(orderId);
  const updatePO = useUpdatePurchaseOrder();
  const receivePO = useReceivePurchaseOrder();
  const [showReceiveConfirm, setShowReceiveConfirm] = useState(false);
  const canReceive = usePermissions().has('purchase_orders.receive');

  const order = data?.data;

  const handleSubmit = async (formData: any) => {
    await updatePO.mutateAsync({ id: orderId, ...formData });
    window.location.hash = '#/pos-management?tab=purchase-orders';
  };

  const handleReceive = async () => {
    await receivePO.mutateAsync(orderId);
    setShowReceiveConfirm(false);
    window.location.hash = '#/pos-management?tab=purchase-orders';
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  if (!order) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Purchase order not found.</div>;
  }

  // A private warehouse's owner opening a buyer's PO: the server returns a
  // restricted shape and rejects receive / update, so show it read-only.
  if (order.isWarehouseOwnerView) {
    return <WarehouseOwnerPurchaseOrderView order={order} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader title={`Purchase Order #${order.orderNumber}`} backTo="#/pos-management?tab=purchase-orders">
        {/* Mark as Received: PO must be at least sent (vendor knows about it) */}
        {canReceive && (order.status === 'sent' || order.status === 'fulfilled') && (
          <button
            onClick={() => setShowReceiveConfirm(true)}
            disabled={receivePO.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {receivePO.isPending ? 'Receiving...' : 'Mark as Received'}
          </button>
        )}
      </PageHeader>
      <PurchaseOrderForm initialData={order} onSubmit={handleSubmit} isSubmitting={updatePO.isPending} />
      {showReceiveConfirm &&
        createPortal(
          <ReceiveConfirmModal
            order={order}
            onClose={() => setShowReceiveConfirm(false)}
            onConfirm={handleReceive}
            isSubmitting={receivePO.isPending}
          />,
          document.body,
        )}
    </div>
  );
}

// ── Receive confirmation modal ──
// Shows each line's purchasing-units → each conversion before the user commits.
// Without this, "Mark as Received" was a one-click stock change with no preview;
// users discovered conversions were wrong only after stock had drifted.

function ReceiveConfirmModal({
  order,
  onClose,
  onConfirm,
  isSubmitting,
}: {
  order: any;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  isSubmitting: boolean;
}) {
  const items: Array<{
    productId: string;
    productName: string;
    unitOfMeasure: string;
    quantity: number;
    baseQty: number;
  }> = (order.items ?? []).map((it: any) => ({
    productId: it.productId,
    productName: it.productName ?? '',
    unitOfMeasure: it.unitOfMeasure ?? 'each',
    quantity: Number(it.quantity) || 0,
    baseQty: Number(it.baseQty) || 1,
  }));
  const totalEach = items.reduce((s, it) => s + Math.round(it.quantity * it.baseQty), 0);
  const hasIdentityConversion = items.some((it) => it.unitOfMeasure.toLowerCase() !== 'each' && it.baseQty <= 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Confirm Receive</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Stock at <span className="font-medium text-foreground">{order.locationName ?? 'this location'}</span> will
              be incremented by the values below. Verify the conversions before confirming.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        {hasIdentityConversion && (
          <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>Heads up:</strong> at least one line is using a 1:1 conversion. If that's wrong (e.g. a case really
            holds N each), cancel, fix the product's units, and try again — once you confirm, stock changes are applied.
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Ordered</th>
                <th className="px-3 py-2 text-right">Conversion</th>
                <th className="px-3 py-2 text-right">+ Stock</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.productId} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.productName}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.quantity} {it.unitOfMeasure}
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground">
                    {it.unitOfMeasure.toLowerCase() === 'each' ? '—' : `1 ${it.unitOfMeasure} = ${it.baseQty} each`}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">+{Math.round(it.quantity * it.baseQty)} each</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30">
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-sm font-medium">
                  Total stock added
                </td>
                <td className="px-3 py-2 text-right text-sm font-semibold">+{totalEach} each</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            <Package className="mr-1 inline h-3 w-3" />
            Spotted a wrong conversion after receive? An admin can correct it from the line item.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-input bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Receiving…' : 'Confirm Receive'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
